/**
 * Full-screen blocker shown to anyone whose account is flagged
 * `must_change_password` (e.g. new hires seeded with a temp password).
 * They cannot access any protected route until they set a new password
 * or log out.
 */
import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { changePassword } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card } from "./ui/card";
import { Loader2, KeyRound, LogOut } from "lucide-react";
import { toast } from "sonner";

export default function ForcePasswordChange() {
  const { user, logout, checkAuth } = useAuth();
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPwd !== confirmPwd) {
      setError("New passwords don't match.");
      return;
    }
    if (newPwd.length < 12) {
      setError("Password must be at least 12 characters.");
      return;
    }
    setSubmitting(true);
    try {
      await changePassword(currentPwd, newPwd);
      toast.success("Password updated — welcome in!");
      // Re-fetch /auth/me so the must_change_password flag flips off.
      await checkAuth();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Password change failed.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-background to-orange-50 p-4">
      <Card className="w-full max-w-md p-8 space-y-6" data-testid="force-password-change-card">
        <div className="text-center space-y-2">
          <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center">
            <KeyRound className="h-6 w-6" />
          </div>
          <h1 className="text-xl font-outfit font-semibold">Set your password</h1>
          <p className="text-sm text-muted-foreground">
            Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}. You're logged in with a
            temporary password. Pick a new one to continue.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="cur-pwd">Temporary password</Label>
            <Input
              id="cur-pwd"
              type="password"
              value={currentPwd}
              onChange={(e) => setCurrentPwd(e.target.value)}
              required
              autoFocus
              autoComplete="current-password"
              data-testid="force-pwd-current"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-pwd">New password</Label>
            <Input
              id="new-pwd"
              type="password"
              value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)}
              required
              minLength={12}
              autoComplete="new-password"
              data-testid="force-pwd-new"
            />
            <p className="text-[11px] text-muted-foreground">
              At least 12 characters. Mix of upper/lower, a digit, and a symbol recommended.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirm-pwd">Confirm new password</Label>
            <Input
              id="confirm-pwd"
              type="password"
              value={confirmPwd}
              onChange={(e) => setConfirmPwd(e.target.value)}
              required
              autoComplete="new-password"
              data-testid="force-pwd-confirm"
            />
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="force-pwd-error">
              {error}
            </p>
          )}

          <div className="flex items-center gap-2">
            <Button type="submit" disabled={submitting} className="flex-1" data-testid="force-pwd-submit">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Set new password"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={logout}
              disabled={submitting}
              data-testid="force-pwd-logout"
            >
              <LogOut className="h-4 w-4 mr-1" /> Log out
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
